import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Service } from '@angular/core';

import { CompanyService } from '@app/shared/services/company.service';
import { environment } from '@environments/environment';
import {
  IFBFileUploadApiResponse,
  IFBJob,
  IFBJobPayload,
  IFBJobResourceGetPageParams,
  IFBJobsAiPage,
  IFBJobsPage
} from '@interfaces';
import { IFBCustomFormTemplate } from '@app/models/custom-form';
import { IFBJobStatisticsNew } from '@app/models/statistics';
import { IFBPublicJob, IFBPublicJobToken, IFBTalentScout } from '@app/models/job-advert';
import { removeEmptyProperties } from '@utils/object-utils';

@Service()
export class JobResource {
  private readonly companyService = inject(CompanyService);
  private readonly http = inject(HttpClient);

  private get url(): string {
    return `https://${environment.API_BASE}/v1/companies/${this.companyService.data.id}/jobs`;
  }

  get(id: string, params = {}) {
    return this.http.get<IFBJob>(this.url + `/${id}`, { params });
  }

  getApplicationFormTemplate(id: string) {
    return this.http.get<IFBCustomFormTemplate>(this.url + `/${id}/template`);
  }

  getPage(params?: IFBJobResourceGetPageParams) {
    return this.http.get<IFBJobsPage>(this.url, { params });
  }

  searchAi(params: {
    offset: number;
    limit: number;
    query?: string;
    hot?: boolean;
    sort?: string;
    department?: string;
    location?: string;
  }) {
    return this.http.get<IFBJobsAiPage>(
      `https://jobs.${environment.SERVICES_BASE}/api/tenants/${this.companyService.data.id}/jobs/search`,
      { params: removeEmptyProperties(params) }
    );
  }

  getStatistics(jobId: string) {
    const headers = new HttpHeaders({
      Accept: 'application/x.firstbird.statistics+json;version=2'
    });
    return this.http.get<IFBJobStatisticsNew>(`${this.url}/${jobId}/statistics`, { headers });
  }

  updateStatus(id: string, value: string) {
    return this.http.put<IFBJob>(`${this.url}/${id}/status`, { value });
  }

  getPublic(id: string, params = {}) {
    const url = `https://${environment.API_BASE}/v1/jobs/${id}`;
    return this.http.get<IFBJob>(url, { params });
  }

  getPublicJobsData(token: string) {
    const url = `https://webapp.${environment.SERVICES_BASE}/candidate/api/jobs?st=${token}`;
    const headers = new HttpHeaders({
      'X-1brd-Host': this.companyService.x1brdHost
    });
    return this.http.get<{ jobs: IFBPublicJob[]; talentScout?: IFBTalentScout }>(url, { headers });
  }

  getPublicJobToken(id: string, token: string) {
    const url = `https://webapp.${environment.SERVICES_BASE}/candidate/api/jobs/${id}?st=${token}`;
    const headers = new HttpHeaders({
      'X-1brd-Host': this.companyService.x1brdHost
    });
    return this.http.get<IFBPublicJobToken>(url, { headers });
  }

  saveDescriptionImage(payload: IFBFileUploadApiResponse) {
    return this.http.post<{ href: string; id: string }>(`${this.url}/description_images`, payload);
  }

  save(job: IFBJobPayload) {
    return this.http.post<IFBJob>(this.url, job);
  }

  update(job: IFBJobPayload) {
    return this.http.put<IFBJob>(`${this.url}/${job.id}`, job);
  }
}
